<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useFontSize } from 'dashboard/composables/useFontSize';
import { useBranding } from 'shared/composables/useBranding';
import { clearCookiesOnLogout } from 'dashboard/store/utils/api.js';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import { parseBoolean } from '@chatwoot/utils';
import { useStorage, useWindowSize } from '@vueuse/core';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import wootConstants from 'dashboard/constants/globals';
import UserProfilePicture from './UserProfilePicture.vue';
import UserBasicDetails from './UserBasicDetails.vue';
import MessageSignature from './MessageSignature.vue';
import FontSize from './FontSize.vue';
import UserLanguageSelect from './UserLanguageSelect.vue';
import HotKeyCard from './HotKeyCard.vue';
import ChangePassword from './ChangePassword.vue';
import NotificationPreferences from './NotificationPreferences.vue';
import AudioNotifications from './AudioNotifications.vue';
import AccessToken from './AccessToken.vue';
import MfaSettingsCard from './MfaSettingsCard.vue';
import UnreadBadgeSettings from './UnreadBadgeSettings.vue';
import ThemeToggle from './ThemeToggle.vue';
import MobileIndex from './MobileIndex.vue';
import Policy from 'dashboard/components/policy.vue';
import SectionLayout from '../account/components/SectionLayout.vue';
import {
  ROLES,
  CONVERSATION_PERMISSIONS,
} from 'dashboard/constants/permissions.js';

export default {
  components: {
    MessageSignature,
    SectionLayout,
    FontSize,
    UserLanguageSelect,
    UserProfilePicture,
    Policy,
    UserBasicDetails,
    HotKeyCard,
    ChangePassword,
    NotificationPreferences,
    AudioNotifications,
    AccessToken,
    MfaSettingsCard,
    UnreadBadgeSettings,
    ThemeToggle,
    MobileIndex,
  },
  setup() {
    const { isEditorHotKeyEnabled, updateUISettings } = useUISettings();
    const { currentFontSize, updateFontSize } = useFontSize();
    const { replaceInstallationName } = useBranding();
    const { width: windowWidth } = useWindowSize();
    const forceDesktopProfileView = useStorage(
      LOCAL_STORAGE_KEYS.PROFILE_FORCE_DESKTOP_VIEW,
      false
    );

    return {
      currentFontSize,
      updateFontSize,
      isEditorHotKeyEnabled,
      updateUISettings,
      replaceInstallationName,
      windowWidth,
      forceDesktopProfileView,
    };
  },
  data() {
    return {
      avatarFile: '',
      avatarUrl: '',
      name: '',
      displayName: '',
      jobTitle: '',
      email: '',
      messageSignature: '',
      hotKeys: [
        {
          key: 'enter',
          title: this.$t(
            'PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.ENTER_KEY.HEADING'
          ),
          description: this.$t(
            'PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.ENTER_KEY.CONTENT'
          ),
          icon: 'i-lucide-corner-down-left',
          keyLabel: 'Enter ↵',
        },
        {
          key: 'cmd_enter',
          title: this.$t(
            'PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.CMD_ENTER_KEY.HEADING'
          ),
          description: this.$t(
            'PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.CMD_ENTER_KEY.CONTENT'
          ),
          icon: 'i-lucide-command',
          keyLabel: '⌘/Ctrl + Enter',
        },
      ],
      notificationPermissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
      audioNotificationPermissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      currentUserId: 'getCurrentUserID',
      globalConfig: 'globalConfig/get',
    }),
    isMfaEnabled() {
      return parseBoolean(window.chatwootConfig?.isMfaEnabled);
    },
    isSmallScreen() {
      return this.windowWidth < wootConstants.SMALL_SCREEN_BREAKPOINT;
    },
    showMobileProfileHome() {
      return this.isSmallScreen && !this.forceDesktopProfileView;
    },
    showForcedDesktopProfileNotice() {
      return this.isSmallScreen && this.forceDesktopProfileView;
    },
  },
  mounted() {
    if (this.currentUserId) {
      this.initializeUser();
    }
  },
  methods: {
    initializeUser() {
      this.name = this.currentUser.name;
      this.email = this.currentUser.email;
      this.avatarUrl = this.currentUser.avatar_url;
      this.displayName = this.currentUser.display_name;
      this.jobTitle =
        this.currentUser.job_title ||
        this.currentUser.custom_attributes?.job_title ||
        '';
      this.messageSignature = this.currentUser.message_signature;
    },
    async dispatchUpdate(payload, successMessage, errorMessage) {
      let alertMessage = '';
      try {
        await this.$store.dispatch('updateProfile', payload);
        alertMessage = successMessage;

        return true;
      } catch (error) {
        alertMessage = parseAPIErrorResponse(error) || errorMessage;

        return false;
      } finally {
        useAlert(alertMessage);
      }
    },
    async updateProfile(userAttributes) {
      const { name, email, displayName, jobTitle } = userAttributes;
      const hasEmailChanged = this.currentUser.email !== email;
      if (name !== undefined) this.name = name;
      if (email !== undefined) this.email = email;
      if (displayName !== undefined) this.displayName = displayName;
      if (jobTitle !== undefined) this.jobTitle = jobTitle;

      const updatePayload = {
        name: this.name,
        email: this.email,
        displayName: this.displayName,
        job_title: this.jobTitle,
        avatar: this.avatarFile,
      };

      const success = await this.dispatchUpdate(
        updatePayload,
        hasEmailChanged
          ? this.$t('PROFILE_SETTINGS.AFTER_EMAIL_CHANGED')
          : this.$t('PROFILE_SETTINGS.UPDATE_SUCCESS'),
        this.$t('RESET_PASSWORD.API.ERROR_MESSAGE')
      );

      if (hasEmailChanged && success) clearCookiesOnLogout();
    },
    async updateSignature(signature) {
      const payload = { message_signature: signature };
      let successMessage = this.$t(
        'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.API_SUCCESS'
      );
      let errorMessage = this.$t(
        'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.API_ERROR'
      );

      await this.dispatchUpdate(payload, successMessage, errorMessage);
    },
    updateProfilePicture({ file, url }) {
      this.avatarFile = file;
      this.avatarUrl = url;
    },
    async deleteProfilePicture() {
      try {
        await this.$store.dispatch('deleteAvatar');
        this.avatarUrl = '';
        this.avatarFile = '';
        useAlert(this.$t('PROFILE_SETTINGS.AVATAR_DELETE_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('PROFILE_SETTINGS.AVATAR_DELETE_FAILED'));
      }
    },
    toggleHotKey(key) {
      this.hotKeys = this.hotKeys.map(hotKey =>
        hotKey.key === key ? { ...hotKey, active: !hotKey.active } : hotKey
      );
      this.updateUISettings({ editor_message_key: key });
      useAlert(this.$t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.UPDATE_SUCCESS'));
    },
    async onCopyToken(value) {
      await copyTextToClipboard(value);
      useAlert(this.$t('COMPONENTS.CODE.COPY_SUCCESSFUL'));
    },
    async resetAccessToken() {
      const success = await this.$store.dispatch('resetAccessToken');
      if (success) {
        useAlert(this.$t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.RESET_SUCCESS'));
      } else {
        useAlert(this.$t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.RESET_ERROR'));
      }
    },
  },
};
</script>

<template>
  <div class="profile-page flex flex-col w-full h-full overflow-hidden">
    <MobileIndex
      v-if="showMobileProfileHome"
      v-model:force-desktop-view="forceDesktopProfileView"
    />

    <template v-else>
    <!-- Header -->
    <header
      class="profile-header flex items-center justify-between px-6 border-b flex-shrink-0"
      style="height: 50px; background: #ffffff; border-bottom-color: #e5e7eb"
    >
      <div class="flex items-center gap-3">
        <span class="i-ph-user-circle-bold size-5" style="color: #6b7280" />
        <h1 class="text-xl font-semibold" style="color: #111827">
          {{ $t('PROFILE_SETTINGS.TITLE') }}
        </h1>
      </div>
    </header>

    <!-- Content -->
    <div class="profile-content flex-1 overflow-y-auto">
      <div
        v-if="showForcedDesktopProfileNotice"
        class="profile-mobile-desktop-notice"
      >
        <span class="profile-mobile-desktop-notice__text">
          Desktop view is enabled for this page.
        </span>
        <button
          type="button"
          class="profile-mobile-desktop-notice__btn"
          @click="forceDesktopProfileView = false"
        >
          Return to mobile view
        </button>
      </div>
      <div class="profile-sections">
        <!-- Profile Picture & Basic Info -->
        <SectionLayout
          :title="$t('PROFILE_SETTINGS.FORM.PROFILE_SECTION.TITLE')"
          :description="$t('PROFILE_SETTINGS.FORM.PROFILE_SECTION.NOTE')"
        >
          <div class="flex flex-col gap-6">
            <UserProfilePicture
              :src="avatarUrl"
              :name="name"
              @change="updateProfilePicture"
              @delete="deleteProfilePicture"
            />
            <UserBasicDetails
              :name="name"
              :display-name="displayName"
              :job-title="jobTitle"
              :email="email"
              :email-enabled="!globalConfig.disableUserProfileUpdate"
              @update-user="updateProfile"
            />
          </div>
        </SectionLayout>

        <!-- Interface Settings -->
        <SectionLayout
          :title="$t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.TITLE')"
          :description="
            replaceInstallationName(
              $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.NOTE')
            )
          "
        >
          <div class="flex flex-col gap-5">
            <ThemeToggle
              label="Theme Mode"
              description="Choose your preferred color theme for the dashboard."
            />
            <FontSize
              :value="currentFontSize"
              :label="
                $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.FONT_SIZE.TITLE')
              "
              :description="
                $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.FONT_SIZE.NOTE')
              "
              @change="updateFontSize"
            />
            <UserLanguageSelect
              :label="
                $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.TITLE')
              "
              :description="
                $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.NOTE')
              "
            />
          </div>
        </SectionLayout>

        <!-- Unread Badge Settings -->
        <SectionLayout
          :title="$t('PROFILE_SETTINGS.FORM.UNREAD_BADGE.TITLE')"
          :description="$t('PROFILE_SETTINGS.FORM.UNREAD_BADGE.NOTE')"
        >
          <UnreadBadgeSettings />
        </SectionLayout>

        <!-- Message Signature -->
        <SectionLayout
          :title="$t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.TITLE')"
          :description="
            $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.NOTE')
          "
        >
          <MessageSignature
            :message-signature="messageSignature"
            @update-signature="updateSignature"
          />
        </SectionLayout>

        <!-- Send Message Hotkey -->
        <SectionLayout
          :title="$t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.TITLE')"
          :description="$t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.NOTE')"
        >
          <div
            class="flex flex-col justify-between w-full gap-5 sm:gap-4 sm:flex-row"
          >
            <button
              v-for="hotKey in hotKeys"
              :key="hotKey.key"
              class="reset-base w-full sm:flex-1 rounded-xl border transition-all duration-200"
              :class="
                isEditorHotKeyEnabled(hotKey.key)
                  ? 'border-[#0066ff] bg-[#fff7ed]'
                  : 'border-[#e5e7eb] hover:border-[#d1d5db] hover:bg-[#f8fafc]'
              "
              @click="toggleHotKey(hotKey.key)"
            >
              <HotKeyCard
                :key="hotKey.title"
                :title="hotKey.title"
                :description="hotKey.description"
                :icon="hotKey.icon"
                :key-label="hotKey.keyLabel"
                :active="isEditorHotKeyEnabled(hotKey.key)"
              />
            </button>
          </div>
        </SectionLayout>

        <!-- Password Section -->
        <SectionLayout
          v-if="!globalConfig.disableUserProfileUpdate"
          :title="$t('PROFILE_SETTINGS.FORM.PASSWORD_SECTION.TITLE')"
          :description="$t('PROFILE_SETTINGS.FORM.PASSWORD_SECTION.NOTE')"
        >
          <ChangePassword />
        </SectionLayout>

        <!-- Security (MFA) Section -->
        <SectionLayout
          v-if="isMfaEnabled"
          :title="$t('PROFILE_SETTINGS.FORM.SECURITY_SECTION.TITLE')"
          :description="$t('PROFILE_SETTINGS.FORM.SECURITY_SECTION.NOTE')"
        >
          <MfaSettingsCard />
        </SectionLayout>

        <!-- Audio Notifications -->
        <Policy :permissions="audioNotificationPermissions">
          <SectionLayout
            :title="
              $t('PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.TITLE')
            "
            :description="
              $t('PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.NOTE')
            "
          >
            <AudioNotifications />
          </SectionLayout>
        </Policy>

        <!-- Notification Preferences -->
        <Policy :permissions="notificationPermissions">
          <SectionLayout
            :title="$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.TITLE')"
            :description="$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.NOTE')"
          >
            <NotificationPreferences />
          </SectionLayout>
        </Policy>

        <!-- Access Token -->
        <SectionLayout
          :title="$t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.TITLE')"
          :description="
            replaceInstallationName(
              $t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.NOTE')
            )
          "
        >
          <AccessToken
            :value="currentUser.access_token"
            @on-copy="onCopyToken"
            @on-reset="resetAccessToken"
          />
        </SectionLayout>
      </div>
    </div>
    </template>
  </div>
</template>

<style scoped>
/* Force Profile settings to match Account settings (always light) */
.profile-page,
.dark .profile-page {
  background: #ffffff !important;
  color: #111827 !important;

  /* Keep WLAB variables in light palette even if body is dark */
  --wlab-bg-primary: #ffffff;
  --wlab-bg-secondary: #f8fafc;
  --wlab-bg-tertiary: #f1f5f9;
  --wlab-bg-input: #ffffff;
  --wlab-bg-hover: #f1f5f9;
  --wlab-bg-card: #ffffff;
  --wlab-bg-sidebar: #f1f5f9;
  --wlab-bg-sidebar-inner: #ffffff;

  --wlab-text-primary: #0f172a;
  --wlab-text-secondary: #475569;
  --wlab-text-tertiary: #64748b;
  --wlab-text-muted: #94a3b8;
  --wlab-text-inverse: #ffffff;

  --wlab-border-default: #e2e8f0;
  --wlab-border-light: #f1f5f9;
  --wlab-border-focus: #0066ff;
  --wlab-accent: #0066ff;
  --wlab-accent-hover: #0058d8;
  --wlab-accent-light: #fff7ed;
}

.profile-header {
  background: #ffffff !important;
  border-bottom-color: #e5e7eb !important;
}

.profile-content {
  background: #ffffff !important;
}

.profile-sections {
  max-width: 100%;
}

.profile-mobile-desktop-notice {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
}

.profile-mobile-desktop-notice__text {
  font-size: 13px;
  line-height: 1.4;
  color: #475569;
}

.profile-mobile-desktop-notice__btn {
  flex-shrink: 0;
  border: 0;
  border-radius: 999px;
  background: #0066ff;
  color: #ffffff;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
}

@media (max-width: 768px) {
  .profile-page {
    background: #f7f7f8 !important;
  }

  .profile-header {
    height: 56px !important;
    padding-right: 16px !important;
    padding-left: 16px !important;
    position: sticky;
    top: 0;
    z-index: 5;
    background: rgba(255, 255, 255, 0.96) !important;
    backdrop-filter: blur(10px);
  }

  .profile-header h1 {
    font-size: 18px !important;
  }

  .profile-content {
    background: #f7f7f8 !important;
  }

  .profile-mobile-desktop-notice {
    margin: 12px 12px 0;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    background: #ffffff;
  }

  .profile-mobile-desktop-notice__btn {
    min-height: 34px;
  }

  .profile-sections {
    padding: 12px 0 24px;
  }

  .profile-sections :deep(.settings-section) {
    margin: 0 12px 12px;
    padding: 16px !important;
    border: 1px solid #e5e7eb !important;
    border-radius: 16px !important;
    background: #ffffff !important;
    box-shadow: 0 10px 24px rgba(17, 24, 39, 0.05);
  }

  .profile-sections :deep(.settings-section__info),
  .profile-sections :deep(.settings-section__content) {
    min-width: 0;
  }

  .profile-sections :deep(.settings-section__content) {
    gap: 14px;
  }

  .profile-sections :deep(.theme-toggle > div) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .profile-sections :deep(.theme-options) {
    width: 100%;
    overflow-x: auto;
  }

  .profile-sections :deep(.theme-btn) {
    flex: 1 0 auto;
    justify-content: center;
    min-width: 68px;
  }

  .profile-sections :deep(.woot-button),
  .profile-sections :deep(.button),
  .profile-sections :deep(button[type='submit']),
  .profile-sections :deep(.submit-button) {
    width: 100% !important;
    justify-content: center !important;
    min-height: 40px;
  }
}

/* Typography overrides */
:deep(.text-n-brand) {
  color: white !important;
}

/* Fix hotkey card styling */
.reset-base {
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  cursor: pointer;
}

/* Style internal components for dark theme */
:deep(.form-section) {
  background-color: transparent;
}

:deep(input[type='text']),
:deep(input[type='email']),
:deep(input[type='password']),
:deep(textarea),
:deep(select) {
  background-color: var(--wlab-bg-input) !important;
  border-color: var(--wlab-border-default) !important;
  color: var(--wlab-text-primary) !important;
}

:deep(input[type='text']:focus),
:deep(input[type='email']:focus),
:deep(input[type='password']:focus),
:deep(textarea:focus),
:deep(select:focus) {
  border-color: rgba(255, 255, 255, 0.3) !important;
  outline: none !important;
}

:deep(label) {
  color: var(--wlab-text-secondary) !important;
}

/* Style buttons - White theme */
:deep(.woot-button),
:deep(.button),
:deep(button[type='submit']),
:deep(.submit-button) {
  background-color: white !important;
  color: black !important;
  border: none !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
}

:deep(.woot-button:hover),
:deep(.button:hover),
:deep(button[type='submit']:hover),
:deep(.submit-button:hover) {
  background-color: #f0f0f0 !important;
}

:deep(.woot-button.clear),
:deep(.button.clear),
:deep(.button--clear) {
  background-color: transparent !important;
  color: var(--wlab-text-secondary) !important;
}

:deep(.woot-button.clear:hover),
:deep(.button.clear:hover),
:deep(.button--clear:hover) {
  color: white !important;
  background-color: var(--wlab-bg-hover) !important;
}

/* Override blue buttons specifically */
:deep(.woot-button.primary),
:deep(.button.primary),
:deep(.bg-woot-500),
:deep(.bg-blue-500),
:deep([class*='bg-woot']),
:deep([class*='bg-blue']) {
  background-color: white !important;
  color: black !important;
}

:deep(.woot-button.primary:hover),
:deep(.button.primary:hover) {
  background-color: #f0f0f0 !important;
}
/* --- Account settings palette alignment (final overrides) --- */
::deep(.text-n-brand) {
  color: #0066ff !important;
}

::deep(.text-n-slate-12),
::deep(.text-n-gray-12) {
  color: #111827 !important;
}

::deep(.text-n-slate-11),
::deep(.text-n-gray-11),
::deep(.text-n-slate-10) {
  color: #4b5563 !important;
}

::deep(.text-n-slate-9) {
  color: #6b7280 !important;
}

::deep(.bg-n-background),
::deep(.bg-n-solid-1) {
  background: #ffffff !important;
}

::deep(.bg-n-solid-2) {
  background: #f9fafb !important;
}

::deep(.bg-n-alpha-2) {
  background: #f1f5f9 !important;
}

::deep(.border-n-weak),
::deep(.border-n-strong) {
  border-color: #e5e7eb !important;
}

::deep(label) {
  color: #374151 !important;
}

::deep(input[type='text']),
::deep(input[type='email']),
::deep(input[type='password']),
::deep(input[type='search']),
::deep(textarea),
::deep(select) {
  background: #ffffff !important;
  color: #111827 !important;
  border: 1px solid #d1d5db !important;
  border-radius: 0.5rem !important;
}

::deep(input[type='text']:focus),
::deep(input[type='email']:focus),
::deep(input[type='password']:focus),
::deep(input[type='search']:focus),
::deep(textarea:focus),
::deep(select:focus) {
  outline: none !important;
  border-color: #0066ff !important;
  box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.1) !important;
}

::deep(.woot-button.clear:hover),
::deep(.button.clear:hover),
::deep(.button--clear:hover) {
  color: #111827 !important;
  background-color: #f1f5f9 !important;
}
</style>
